import React, { useState, useMemo, useEffect } from 'react';
import { TableProps, GetDataTableResponse } from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import api from 'api';
import './table-component.scss';

interface SortConfig {
  key: string | null;
  direction: 'asc' | 'desc';
}

// Extended interface for enhanced table functionality
export interface EnhancedTableProps extends TableProps {
  tagColumns?: string[];
  threadId?: string;
}

/**
 * Hook to fetch data table by ID
 */
const useDataTable = (dataTableId?: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<{
    headingArray: string[];
    valuesArrays: string[][];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!dataTableId) return;

    const fetchDataTable = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get<GetDataTableResponse>(
          `/data-tables/${dataTableId}`,
        );

        if (response.data.success && response.data.dataTable) {
          setData({
            headingArray: response.data.dataTable.headings,
            valuesArrays: response.data.dataTable.values,
          });
        } else {
          throw new Error(response.data.message || 'Failed to load data table');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchDataTable();
  }, [dataTableId, getAccessTokenSilently]);

  return { data, loading, error };
};

/**
 * Table component for our DSL with modern styling, pagination, and sorting
 */
const TableComponentBase: React.FC<EnhancedTableProps> = ({
  headingArray,
  valuesArrays,
  dataTableId,
  striped = false,
  hover = false,
  bordered = false,
  responsive = false,
  tagColumns = [],
  threadId,
  ...props
}) => {
  const { data: fetchedData, loading, error } = useDataTable(dataTableId);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'asc',
  });
  const rowsPerPage = 10;

  // Determine data source - either from props or fetched data
  const actualHeadings =
    dataTableId && fetchedData ? fetchedData.headingArray : headingArray;
  const actualValues =
    dataTableId && fetchedData ? fetchedData.valuesArrays : valuesArrays;

  // Ensure headingArray is an array
  const safeHeadings = Array.isArray(actualHeadings) ? actualHeadings : [];

  const safeValues = Array.isArray(actualValues) ? actualValues : [];
  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return safeValues;

    const columnIndex = safeHeadings.indexOf(sortConfig.key);
    if (columnIndex === -1) return safeValues;

    return [...safeValues].sort((a: string[], b: string[]) => {
      const aVal = a[columnIndex];
      const bVal = b[columnIndex];

      // Handle different data types
      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.toLowerCase().localeCompare(bVal.toLowerCase());
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [actualValues, sortConfig, safeHeadings]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, currentPage]);

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleSort = (key: string) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const renderCellContent = (value: string, heading: string) => {
    if (value === null || value === undefined) return '';

    // Check if this column should be styled as tags
    const isTagColumn = tagColumns.includes(heading);

    if (isTagColumn) {
      // Handle status-like values (Active/Inactive)
      if (typeof value === 'string') {
        const lowerValue = value.toLowerCase();
        if (lowerValue === 'active' || lowerValue === 'inactive') {
          const tagClass =
            lowerValue === 'active' ? 'tag active' : 'tag inactive';
          return <span className={tagClass}>{value}</span>;
        }
      }

      // Handle array of tags
      if (Array.isArray(value)) {
        return (
          <>
            {value.map((tag, index) => (
              <span key={index} className='tag tag-pill'>
                {tag}
              </span>
            ))}
          </>
        );
      }

      // Handle comma-separated string of tags
      if (typeof value === 'string' && value.includes(',')) {
        return (
          <>
            {value.split(',').map((tag, index) => (
              <span key={index} className='tag tag-pill'>
                {tag.trim()}
              </span>
            ))}
          </>
        );
      }

      // Handle single tag value
      if (typeof value === 'string' && value.trim()) {
        return <span className='tag tag-pill'>{value}</span>;
      }
    }

    return String(value);
  };

  // Handle loading state
  if (dataTableId && loading) {
    return <div className='table-loading'>Loading data table...</div>;
  }

  // Handle error state
  if (dataTableId && error) {
    return <div className='table-error'>Error loading data table: {error}</div>;
  }

  // Handle empty data
  if (safeHeadings.length === 0 || !actualValues || actualValues.length === 0) {
    return <div className='table-empty'>No data to display</div>;
  }

  return (
    <div className='table-component'>
      <div className='table-container'>
        <div className='scroll-wrapper'>
          <div className='table-shadow-wrap'>
            <table className='hero-table'>
              <thead id='table-head'>
                <tr>
                  {safeHeadings.map((heading, index) => (
                    <th
                      key={`heading-${index}`}
                      onClick={() => handleSort(heading)}
                      style={{ cursor: 'pointer' }}
                    >
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody id='table-body'>
                {paginatedData.map((row: any[], rowIndex) => (
                  <tr key={`row-${rowIndex}`}>
                    {safeHeadings.map((heading, cellIndex) => (
                      <td key={`cell-${rowIndex}-${cellIndex}`}>
                        {renderCellContent(row[cellIndex], heading)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className='pagination'>
            <button
              id='prev-page'
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              ←
            </button>
            <span id='page-info'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              id='next-page'
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Create memoized version to prevent unnecessary re-renders
const TableComponent = React.memo(TableComponentBase);

export default TableComponent;
